import type { AdminCommand } from '@thoughtspot/admin';
import {
    buildCommandRegistry,
    getCustomisationItemTabs,
    getSideBarConfig,
    getSidebarSectionsFromResponse,
    getUserManagementItemTabs,
    parseAdminItemPath,
    tabIdToSlug,
    useGetSidebarConfigLazyNoCache,
    useGetStatusLazy,
    USER_MANAGEMENT_TAB_IDS,
} from '@thoughtspot/admin';
import { isOrgsOnFTEnabled } from '@thoughtspot/feature-service';
import {
    SpotlightItem,
    SpotlightItemType,
} from '@thoughtspot/global-nav';
import { useTranslation } from '@thoughtspot/i18n';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import {
    getCurrentOrgId,
    hasPrivilege,
    isAdminModernExperienceEnabled,
    isAdminRoutingEnabled,
    isAdminTabVisible,
    isComsEnabled,
    isCopilotEnabled,
    isCustomChartEnabled,
    isCustomizeEmailFromAdminEnabled,
    isCustomRegionEnabled,
    isModularHomeExperienceEnabled,
    isOrgUiEnabled,
    isReactshellEnabled,
    isRolesEnabled,
    isSaaSCluster,
    isVariablesUIEnabled,
    PRIVILEGES,
} from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticatedState } from '/@services/system/login-service';
import { ADMIN_COMMAND_PALETTE_NAVIGATE_EVENT } from '/@utils/admin-events';

/** Shape of data.Data returned by useGetStatusLazy — derived from the hook's return type. */
type AdminStatusData = NonNullable<ReturnType<typeof useGetStatusLazy>['data']['Data']>;

/** Matches the `strings` alias used by getSideBarConfig: (x: string) => string */
type TranslateFn = (key: string) => string;

const ADMIN_ITEM_PREFIX = 'admin-cmd-';

const CONTEXT_ALL_ID = 'all-org';
const CONTEXT_PRIMARY_ID = 'primary-org';

const STYLE_CUSTOMIZATION_CMD_ID = 'settings-style-customization-react';
const THOUGHTSPOT_AI_CMD_ID = 'tscli-ui-sage_feature';

const THOUGHTSPOT_AI_SUB_ITEMS = [
    {
        id: 'ts-ai-spotter-version',
        labelKey: 'spotterExperience.title',
        keywords: ['spotter', 'version', 'ai', 'model'],
    },
    {
        id: 'ts-ai-activation-targets',
        labelKey: 'spotterActivationTargets.title',
        keywords: ['spotter', 'access', 'users', 'activation'],
    },
    {
        id: 'ts-ai-spotter3-capabilities',
        labelKey: 'spotter3Capabilities.title',
        keywords: ['spotter', 'capabilities', 'features', 'ai'],
    },
    {
        id: 'ts-ai-user-experience',
        labelKey: 'configureSpotterUserExperience.title',
        keywords: ['spotter', 'user', 'experience', 'configure'],
    },
    {
        id: 'ts-ai-viz-settings',
        labelKey: 'spotterVizSettings.title',
        keywords: ['spotter', 'viz', 'visualization', 'settings'],
    },
    {
        id: 'ts-ai-spotter-model',
        labelKey: 'spotterModel.title',
        keywords: ['spotter', 'model', 'ai'],
    },
    {
        id: 'ts-ai-additional-features',
        labelKey: 'additionalAiFeatures.title',
        keywords: ['ai', 'features', 'additional', 'spotter'],
    },
    {
        id: 'ts-ai-llm-connections',
        labelKey: 'aiModelType.title',
        keywords: ['llm', 'model', 'connections', 'ai'],
    },
] as const;

// Search & SpotIQ's nav item id — and therefore its URL slug — differs by
// experience, so the commandId must match the one the active sidebar emits:
//  • Modern: the consolidated sidebar uses 'search-spot-iq' (identity slug
//    'search-spot-iq'), matching sidebar-config alias search_spot_iq.
//  • Software (non-modern): it is a standalone tscli tab 'tscli-ui-search'
//    (slug 'search'). Using the modern id here builds #/admin/.../search-spot-iq,
//    which slugToTabId can't resolve to the software screen — the pane goes
//    blank while the sidebar tab click lands on #/admin/.../search correctly.
const SEARCH_SPOTIQ_MODERN_CMD_ID = 'search-spot-iq';
const SEARCH_SPOTIQ_LEGACY_CMD_ID = 'tscli-ui-search';
const getSearchSpotIQCmdId = (): string =>
    isAdminModernExperienceEnabled()
        ? SEARCH_SPOTIQ_MODERN_CMD_ID
        : SEARCH_SPOTIQ_LEGACY_CMD_ID;
// Administration's target depends on the experience:
//  • Modern: consolidated under the Application Settings tab (general sub-tab),
//    with no standalone Administration screen — target the Application Settings
//    nav item id, so it lands on #/admin/[all-orgs/]app-settings/general.
//  • Software (non-modern): Administration is still a standalone tscli tab —
//    keep its original nav item id.
const ADMINISTRATION_MODERN_CMD_ID = 'application-settings';
const ADMINISTRATION_LEGACY_CMD_ID = 'tscli-ui-administration';
const getAdministrationCmdId = (): string =>
    isAdminModernExperienceEnabled()
        ? ADMINISTRATION_MODERN_CMD_ID
        : ADMINISTRATION_LEGACY_CMD_ID;

const SEARCH_SPOTIQ_SUB_ITEMS = [
    {
        id: 'search-index-settings',
        labelKey: 'indexSetting.title',
        keywords: ['index', 'search', 'indexing', 'settings'],
    },
    {
        id: 'search-formula-settings',
        labelKey: 'formulaSetting.title',
        keywords: ['formula', 'search', 'settings', 'passthrough'],
    },
    {
        id: 'search-query-settings',
        labelKey: 'querySetting.title',
        keywords: ['query', 'search', 'settings', 'columns'],
    },
    {
        id: 'search-spotiq',
        labelKey: 'spotiq.title',
        keywords: ['spotiq', 'ai', 'insights', 'analysis', 'search'],
    },
] as const;

const ADMINISTRATION_SUB_ITEMS = [
    {
        id: 'admin-org-switcher',
        labelKey: 'showOrgSwitcherToUsersWithMultipleOrgs.title',
        keywords: ['org', 'switcher', 'users', 'multiple', 'administration'],
    },
    {
        id: 'admin-org-url-sharing',
        labelKey: 'orgPerUrlSharing.title',
        keywords: ['org', 'url', 'sharing', 'administration'],
    },
    {
        id: 'admin-selective-access',
        labelKey: 'enableSelectiveUserAccessRestriction.title',
        keywords: ['selective', 'user', 'access', 'restriction', 'administration'],
    },
    {
        id: 'admin-intercom',
        labelKey: 'intercomEnabled.title',
        keywords: ['intercom', 'support', 'in-app', 'administration'],
    },
] as const;

const STYLE_CUSTOMIZATION_SUB_ITEMS = [
    {
        id: 'style-logo-default',
        labelKey: 'styleCustomizer.appLogoSelector.HEADER',
        keywords: ['logo', 'favicon', 'branding', 'style', 'application'],
    },
    {
        id: 'style-logo-wide',
        labelKey: 'styleCustomizer.wideAppLogoSelector.HEADER',
        keywords: ['logo', 'wide', 'branding', 'style', 'application'],
    },
    {
        id: 'style-chart-fonts',
        labelKey: 'styleCustomizer.chartFontSelector.HEADER',
        keywords: ['font', 'chart', 'visualization', 'typography', 'style'],
    },
    {
        id: 'style-table-fonts',
        labelKey: 'styleCustomizer.tableFontSelector.HEADER',
        keywords: ['font', 'table', 'visualization', 'typography', 'style'],
    },
    {
        id: 'style-nav-color',
        labelKey: 'styleCustomizer.appPanelColorSelector.HEADER',
        keywords: ['navigation', 'panel', 'color', 'style', 'customize'],
    },
    {
        id: 'style-chart-colors',
        labelKey: 'styleCustomizer.chartColorPaletteEditor.HEADER',
        keywords: ['chart', 'color', 'palette', 'style', 'customize'],
    },
    {
        id: 'style-footer-text',
        labelKey: 'styleCustomizer.footerText.HEADER',
        keywords: ['footer', 'text', 'embed', 'embedded', 'style'],
    },
] as const;

const HOMEPAGE_DISCOVERY_CMD_ID = 'homepage-and-discovery';
const CHART_CUSTOMIZATION_CMD_ID = 'chart-customization';

const HOMEPAGE_DISCOVERY_SUB_ITEMS = [
    {
        id: 'homepage-alert-banner',
        labelKey: 'admin.home.announcement.config.title',
        keywords: ['alert', 'banner', 'announcement', 'homepage', 'custom'],
    },
    {
        id: 'homepage-customize',
        labelKey: 'homepage.modular.customize.button',
        keywords: ['customize', 'homepage', 'modules', 'home'],
    },
    {
        id: 'homepage-auto-translate',
        labelKey: 'adminSection.autoTranslation.title',
        keywords: ['translate', 'auto', 'liveboards', 'answers', 'language'],
    },
    {
        id: 'homepage-manual-translate',
        labelKey: 'adminSection.manualTranslation.title',
        keywords: ['translate', 'csv', 'liveboards', 'answers', 'manual', 'language'],
    },
] as const;

const CHART_CUSTOMIZATION_SUB_ITEMS = [
    {
        id: 'chart-custom-maps',
        labelKey: 'adminSection.chartCustomization.geomaps.title',
        keywords: ['maps', 'geo', 'custom', 'chart', 'geography'],
    },
    {
        id: 'chart-custom-charts',
        labelKey: 'adminSection.chartCustomization.customCharts.title',
        keywords: ['charts', 'custom', 'byoc', 'visualization'],
    },
] as const;

// Pages whose in-page tabs (not settings-within-a-tab) should each be a
// navigable Cmd-K entry. Unlike the *_SUB_ITEMS above — which are individual
// settings that all land on one screen — these target a specific sub-tab via
// meta.subTabSlug, appended to the page URL by handleAdminItemSelect.
const USER_MANAGEMENT_CMD_ID = 'modern-user-management';
const USER_MANAGEMENT_PAGE_LABEL_KEY = 'adminSection.userManagement.tabHeader';
const FEATURE_MANAGEMENT_CMD_ID = 'feature-management';
const FEATURE_MANAGEMENT_PAGE_LABEL_KEY = 'adminSection.feature_management.title';

interface PageTabDef {
    subTabSlug: string;
    labelKey: string;
    keywords: readonly string[];
}

// slug values match USER_MANAGEMENT_TAB_IDS and the user-management container's
// URL sub-tab regex; labels mirror the container's tab headers.
const USER_MANAGEMENT_TABS: readonly PageTabDef[] = [
    {
        subTabSlug: USER_MANAGEMENT_TAB_IDS.users,
        labelKey: 'adminSection.userManagement.tabUsers.name',
        keywords: ['users', 'user', 'members', 'accounts', 'people'],
    },
    {
        subTabSlug: USER_MANAGEMENT_TAB_IDS.groups,
        labelKey: 'adminSection.userManagement.tabGroups.name',
        keywords: ['groups', 'group', 'teams'],
    },
    {
        subTabSlug: USER_MANAGEMENT_TAB_IDS.roles,
        labelKey: 'adminSection.userManagement.tabRoles.name',
        keywords: ['roles', 'role', 'permissions', 'privileges'],
    },
    {
        subTabSlug: USER_MANAGEMENT_TAB_IDS.authentication,
        labelKey: 'adminSection.authentication.name',
        keywords: ['authentication', 'auth', 'login', 'sso', 'saml'],
    },
] as const;

// slug values match feature-management container's SUBTAB_ID_TO_SLUG. Only
// Early Access exists in an org view; All Orgs shows both tabs.
const FEATURE_MANAGEMENT_GENERAL_TAB: PageTabDef = {
    subTabSlug: 'general',
    labelKey: 'adminSection.feature_management.tabs.generalAccess',
    keywords: ['general', 'access', 'feature', 'management'],
};
const FEATURE_MANAGEMENT_EARLY_TAB: PageTabDef = {
    subTabSlug: 'early-access',
    labelKey: 'adminSection.feature_management.tabs.earlyAccess',
    keywords: ['early', 'access', 'feature', 'management', 'beta'],
};

// Consolidated Customization page (settings-style-customization-react): every
// customization TAB surfaced directly as a Cmd-K entry, gated by the
// server-decided itemTabs. (The settings-level CUSTOMIZATION_FAMILIES entries —
// Application Logo, Custom maps, alert banner, etc. — still exist as finer,
// search-by-setting shortcuts that land on the same tabs.) subTabSlug is the
// container URL slug; itemTabId is the id the BE emits in itemTabs.
const CUSTOMIZATION_PAGE_LABEL_KEY = 'adminSection.sidebar.customisation';
const CUSTOMIZATION_TAB_ENTRIES: readonly (PageTabDef & {
    itemTabId: string;
})[] = [
    {
        subTabSlug: 'styles',
        itemTabId: 'style',
        labelKey: 'adminSection.customizations.tabs.style',
        keywords: ['style', 'styles', 'logo', 'fonts', 'customization'],
    },
    {
        subTabSlug: 'charts',
        itemTabId: 'chart',
        labelKey: 'adminSection.customizations.tabs.chart',
        keywords: ['chart', 'charts', 'maps', 'custom', 'customization'],
    },
    {
        subTabSlug: 'app-experience',
        itemTabId: 'homepage',
        labelKey: 'adminSection.customizations.tabs.homepage',
        keywords: [
            'app',
            'experience',
            'home',
            'homepage',
            'customization',
        ],
    },
    {
        subTabSlug: 'email',
        itemTabId: 'email',
        labelKey: 'adminSection.customizations.tabs.email',
        keywords: ['email', 'customization', 'whitelabel', 'branding'],
    },
    {
        subTabSlug: 'help',
        itemTabId: 'help',
        labelKey: 'adminSection.customizations.tabs.help',
        keywords: ['help', 'customization', 'support', 'documentation'],
    },
];

// Consolidated Application Settings page and its tabs. The modern GetSidebarConfig
// collapses Data modeling / CSV upload into this one item, so surface them as tab
// deep-links, gated by the raw GetStatus application_settings flags.
const APP_SETTINGS_CMD_ID = 'application-settings';
const APP_SETTINGS_PAGE_LABEL_KEY = 'adminSection.application_settings.name';
const APP_SETTINGS_TAB_ENTRIES: readonly (PageTabDef & {
    statusFlag: string;
})[] = [
    {
        subTabSlug: 'modeling',
        statusFlag: 'data_modelling',
        labelKey: 'adminSection.data_modeling.name',
        keywords: ['data', 'model', 'modeling', 'modelling'],
    },
    {
        subTabSlug: 'csv-upload',
        statusFlag: 'csv_upload',
        labelKey: 'csvUpload.admin.leftPanel.title',
        keywords: ['csv', 'upload', 'import', 'data'],
    },
];

// Canonical admin deep-link URL for a spotlight item — the same URL
// handleAdminItemSelect navigates to. tabIdToSlug maps the screenID to its slug
// (which may itself contain a sub-tab, e.g. customization/charts); an explicit
// subTabSlug (User/Feature Management tabs) is appended after it.
function buildAdminItemUrl(
    commandId: string,
    orgContext?: string,
    subTabSlug?: string,
): string {
    const prefix = orgContext === 'all-org' ? '/admin/all-orgs' : '/admin';
    const sub = subTabSlug ? `/${subTabSlug}` : '';
    return `${prefix}/${tabIdToSlug(commandId)}${sub}`;
}

// The value persisted as a recent's `path`, chosen by whether admin routing is
// on. Both are decoded back by resolveAdminMeta.
//  • Routing ON: the real canonical URL — the recent carries a genuine
//    deep-link and navigation re-derives the screen from it via slugToTabId.
//  • Routing OFF (software/legacy): navigation never uses the URL —
//    handleAdminItemSelect drives it from sessionStorage + the command-palette
//    event by raw id — so we persist the ids directly as a
//    'commandId|sectionId|orgContext[|subTabSlug]' pipe encoding, which is
//    immune to the slug round-trip mismatches a URL can hit in software. The
//    sub-tab is appended only when present, so plain items read
//    'commandId||orgContext' as before sub-tab support existed.
function buildAdminRecentPath(meta: {
    commandId: string;
    sectionId?: string;
    orgContext?: string;
    subTabSlug?: string;
}): string {
    if (isAdminRoutingEnabled()) {
        return buildAdminItemUrl(
            meta.commandId,
            meta.orgContext,
            meta.subTabSlug,
        );
    }
    const parts = [meta.commandId, meta.sectionId ?? '', meta.orgContext ?? ''];
    if (meta.subTabSlug) parts.push(meta.subTabSlug);
    return parts.join('|');
}

// Builds one navigable Cmd-K entry per in-page tab. descriptionKeys is the
// breadcrumb (context / group / page); each part re-translates at render time
// (group is an already-translated section string — tStr falls back to it).
function buildPageTabItems(
    tabs: readonly PageTabDef[],
    commandId: string,
    descriptionKeys: string[],
    extraKeywords: string[],
    t: TranslateFn,
    orgContext?: string,
): AdminSpotlightItem[] {
    return tabs.map(({ subTabSlug, labelKey, keywords }) => ({
        id: `${ADMIN_ITEM_PREFIX}${commandId}-${subTabSlug}${
            orgContext ? `-${orgContext}` : ''
        }`,
        label: t(labelKey),
        labelKey,
        descriptionKeys,
        icon: IconID.COG,
        type: SpotlightItemType.ADMIN,
        keywords: [...keywords, ...extraKeywords],
        meta: {
            commandId,
            orgContext,
            subTabSlug,
            url: buildAdminRecentPath({ commandId, orgContext, subTabSlug }),
        },
    }));
}

// Builds page-tab entries (User Management, Feature Management, and the
// Customization / Application Settings sub-tabs that the modern GetSidebarConfig
// consolidates into a single item) for one org context, reading each page's real
// section label from its command so the breadcrumb reflects the sidebar group.
function buildPageTabsForContext(
    cmds: AdminCommand[],
    sidebarResponse: unknown,
    isAllOrg: boolean,
    t: TranslateFn,
    appSettings?: Record<string, unknown>,
    contextLabelKey?: string,
    orgContext?: string,
): AdminSpotlightItem[] {
    const items: AdminSpotlightItem[] = [];

    const umCmd = cmds.find(c => c.id === USER_MANAGEMENT_CMD_ID);
    if (umCmd) {
        // Prefer the server-decided tab set; fall back to all tabs (minus the
        // per-org Groups/Roles in an all-orgs view) when itemTabs is absent.
        const serverTabs = getUserManagementItemTabs(sidebarResponse as any);
        const allowed = serverTabs.length
            ? serverTabs
            : USER_MANAGEMENT_TABS.map(x => x.subTabSlug).filter(
                  slug =>
                      !isAllOrg ||
                      (slug !== USER_MANAGEMENT_TAB_IDS.groups &&
                          slug !== USER_MANAGEMENT_TAB_IDS.roles),
              );
        const tabs = USER_MANAGEMENT_TABS.filter(x =>
            allowed.includes(x.subTabSlug),
        );
        items.push(
            ...buildPageTabItems(
                tabs,
                USER_MANAGEMENT_CMD_ID,
                [
                    ...(contextLabelKey ? [contextLabelKey] : []),
                    ...(umCmd.section ? [umCmd.section] : []),
                    USER_MANAGEMENT_PAGE_LABEL_KEY,
                ],
                umCmd.searchText.split(/\s+/).filter(Boolean),
                t,
                orgContext,
            ),
        );
    }

    const fmCmd = cmds.find(c => c.id === FEATURE_MANAGEMENT_CMD_ID);
    if (fmCmd) {
        const tabs = isAllOrg
            ? [FEATURE_MANAGEMENT_GENERAL_TAB, FEATURE_MANAGEMENT_EARLY_TAB]
            : [FEATURE_MANAGEMENT_EARLY_TAB];
        items.push(
            ...buildPageTabItems(
                tabs,
                FEATURE_MANAGEMENT_CMD_ID,
                [
                    ...(contextLabelKey ? [contextLabelKey] : []),
                    ...(fmCmd.section ? [fmCmd.section] : []),
                    FEATURE_MANAGEMENT_PAGE_LABEL_KEY,
                ],
                fmCmd.searchText.split(/\s+/).filter(Boolean),
                t,
                orgContext,
            ),
        );
    }

    // Customization: every tab the server returns in itemTabs is surfaced as a
    // page-level Cmd-K entry (styles/charts/homepage/email/help — the filter is
    // purely custTabs.includes). CUSTOMIZATION_FAMILIES separately provides the
    // finer per-setting shortcuts (Application Logo, custom maps, etc.) that
    // land on the same tabs; the two are complementary, not mutually exclusive.
    const custCmd = cmds.find(c => c.id === STYLE_CUSTOMIZATION_CMD_ID);
    if (custCmd) {
        const custTabs = getCustomisationItemTabs(sidebarResponse as any) ?? [];
        const tabs = CUSTOMIZATION_TAB_ENTRIES.filter(x =>
            custTabs.includes(x.itemTabId),
        );
        if (tabs.length) {
            items.push(
                ...buildPageTabItems(
                    tabs,
                    STYLE_CUSTOMIZATION_CMD_ID,
                    [
                        ...(contextLabelKey ? [contextLabelKey] : []),
                        ...(custCmd.section ? [custCmd.section] : []),
                        CUSTOMIZATION_PAGE_LABEL_KEY,
                    ],
                    custCmd.searchText.split(/\s+/).filter(Boolean),
                    t,
                    orgContext,
                ),
            );
        }
    }

    // Application Settings: Data modeling / CSV upload tabs, gated by the raw
    // GetStatus application_settings flags (no server itemTabs for this item).
    const appCmd = cmds.find(c => c.id === APP_SETTINGS_CMD_ID);
    if (appCmd) {
        const tabs = APP_SETTINGS_TAB_ENTRIES.filter(
            x => !!appSettings?.[x.statusFlag],
        );
        if (tabs.length) {
            items.push(
                ...buildPageTabItems(
                    tabs,
                    APP_SETTINGS_CMD_ID,
                    [
                        ...(contextLabelKey ? [contextLabelKey] : []),
                        ...(appCmd.section ? [appCmd.section] : []),
                        APP_SETTINGS_PAGE_LABEL_KEY,
                    ],
                    appCmd.searchText.split(/\s+/).filter(Boolean),
                    t,
                    orgContext,
                ),
            );
        }
    }

    return items;
}

function hasCommandWithId(commands: AdminCommand[], id: string): boolean {
    return commands.some(cmd => cmd.id === id || cmd.parentId === id);
}


// The consolidated Customization page id (GetSidebarConfig alias `customisation`);
// its section + label form the breadcrumb group/page for all style/chart/homepage
// settings, whose own screen ids GetSidebarConfig folds into this one tab set.
const CUSTOMIZATION_OWNER_ID = STYLE_CUSTOMIZATION_CMD_ID;

interface ExtraFamily {
    items: readonly {
        id: string;
        labelKey: string;
        keywords: readonly string[];
    }[];
    // Navigation target (meta.commandId) — mapped to a URL by tabIdToSlug.
    navCmdId: string;
    // Display command whose section (group) + label (page) form the breadcrumb.
    breadcrumbOwnerId: string;
    // Optional in-page tab segment appended after the page (customization tabs).
    tabLabelKey?: string;
    // Legacy breadcrumb tail used in software / when the owner isn't found.
    fallbackSectionKey: string;
}

// Customization settings — gated by their own screen id (present in the
// GetStatus commands), breadcrumb owned by the consolidated Customization page.
const CUSTOMIZATION_FAMILIES: readonly ExtraFamily[] = [
    {
        items: STYLE_CUSTOMIZATION_SUB_ITEMS,
        navCmdId: STYLE_CUSTOMIZATION_CMD_ID,
        breadcrumbOwnerId: CUSTOMIZATION_OWNER_ID,
        tabLabelKey: 'adminSection.customizations.tabs.style',
        fallbackSectionKey: 'adminSection.styleCustomization.tabHeader',
    },
    {
        items: CHART_CUSTOMIZATION_SUB_ITEMS,
        navCmdId: CHART_CUSTOMIZATION_CMD_ID,
        breadcrumbOwnerId: CUSTOMIZATION_OWNER_ID,
        tabLabelKey: 'adminSection.customizations.tabs.chart',
        fallbackSectionKey: 'adminSection.chartCustomization.title',
    },
    {
        items: HOMEPAGE_DISCOVERY_SUB_ITEMS,
        navCmdId: HOMEPAGE_DISCOVERY_CMD_ID,
        breadcrumbOwnerId: CUSTOMIZATION_OWNER_ID,
        tabLabelKey: 'adminSection.customizations.tabs.homepage',
        fallbackSectionKey: 'adminSection.homePageAppExperience.title',
    },
] as const;

// Builds the breadcrumb (descriptionKeys) for an extra family. In modern SaaS it
// mirrors the visual sidebar — context / <group> / <page> [/ <tab>] — read from
// the owning display command. Otherwise it keeps the legacy Application Settings
// path so software doesn't regress. Group/page are already-translated section
// strings (tStr falls back to the string); context/tab are i18n keys.
function buildExtraCrumb(
    displayCmds: AdminCommand[],
    ownerId: string,
    tabLabelKey: string | undefined,
    fallbackSectionKey: string,
    contextLabelKey?: string,
): string[] {
    const ctx = contextLabelKey ? [contextLabelKey] : [];
    const owner = displayCmds.find(c => c.id === ownerId);
    if (isAdminModernExperienceEnabled() && owner) {
        return [
            ...ctx,
            ...(owner.section ? [owner.section] : []),
            ...(owner.label ? [owner.label] : []),
            ...(tabLabelKey ? [tabLabelKey] : []),
        ];
    }
    return [
        ...ctx,
        'adminSection.application_settings.name',
        fallbackSectionKey,
    ];
}

// Generic extra-settings builder: one Cmd-K entry per setting, sharing a
// precomputed breadcrumb and navigating to the family's page (navCmdId).
function buildExtraSubItems(
    items: readonly {
        id: string;
        labelKey: string;
        keywords: readonly string[];
    }[],
    navCmdId: string,
    descriptionKeys: string[],
    orgContext: string | undefined,
    t: TranslateFn,
): AdminSpotlightItem[] {
    return items.map(({ id, labelKey, keywords }) => ({
        id: `${ADMIN_ITEM_PREFIX}${id}${orgContext ? `-${orgContext}` : ''}`,
        label: t(labelKey),
        labelKey,
        descriptionKeys,
        icon: IconID.COG,
        type: SpotlightItemType.ADMIN,
        keywords: [...keywords],
        meta: {
            commandId: navCmdId,
            orgContext,
            url: buildAdminRecentPath({ commandId: navCmdId, orgContext }),
        },
    }));
}

function getDefaultStatusConfig(contextId?: string): AdminStatusData {
    const orgId = getCurrentOrgId();
    const orgsEnabled =
        isOrgUiEnabled() && (orgId === -1 || orgId === 0);

    let resolvedContextId = contextId;
    if (resolvedContextId === undefined) {
        if (orgId === -1) {
            resolvedContextId = CONTEXT_ALL_ID;
        } else if (orgId === 0) {
            resolvedContextId = CONTEXT_PRIMARY_ID;
        } else {
            resolvedContextId = '';
        }
    }

    let isSwitchOrgTabVisible = false;
    if (orgsEnabled) {
        if (isRolesEnabled()) {
            isSwitchOrgTabVisible =
                hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                (hasPrivilege(PRIVILEGES.USER_ADMINISTRATION) ||
                    hasPrivilege(PRIVILEGES.GROUP_ADMINISTRATION) ||
                    hasPrivilege(PRIVILEGES.ROLE_ADMINISTRATION));
        } else {
            isSwitchOrgTabVisible = true;
        }
    }

    if (isSwitchOrgTabVisible) {
        if (resolvedContextId === CONTEXT_ALL_ID) {
            return {
                status: {
                    org_management: { org: true },
                    user_management: { users: true },
                    authentication: { saml: true },
                    application_settings: {
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations:
                            isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                        variables:
                            hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                            isVariablesUIEnabled(),
                    },
                },
                __typename: 'Admin__Data' as const,
            };
        } 
            return {
                status: {
                    user_management: { users: true, groups: true, roles: true },
                    application_settings: {
                        chart_customization:
                            isCustomChartEnabled() || isCustomRegionEnabled(),
                        homepage_and_discovery: isModularHomeExperienceEnabled(),
                        email_customizations:
                            isComsEnabled() &&
                            isCustomizeEmailFromAdminEnabled(),
                    },
                },
                __typename: 'Admin__Data' as const,
            };
        
    } if (orgsEnabled) {
        return {
            status: {
                user_management: { users: true, groups: true, roles: true },
                org_management: hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION)
                    ? { org: true }
                    : { org: false },
                application_settings: {
                    chart_customization:
                        isCustomChartEnabled() || isCustomRegionEnabled(),
                    homepage_and_discovery: isModularHomeExperienceEnabled(),
                    email_customizations:
                        isComsEnabled() && isCustomizeEmailFromAdminEnabled(),
                    variables:
                        hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                        isVariablesUIEnabled() &&
                        isOrgUiEnabled() &&
                        getCurrentOrgId() === -1,
                },
            },
            __typename: 'Admin__Data' as const,
        };
    } 
        return {
            status: {
                user_management: { users: true, groups: true, roles: true },
                teams: { pricing: true },
                application_settings: {
                    chart_customization:
                        isCustomChartEnabled() || isCustomRegionEnabled(),
                    homepage_and_discovery: isModularHomeExperienceEnabled(),
                    email_customizations:
                        isComsEnabled() && isCustomizeEmailFromAdminEnabled(),
                    variables:
                        hasPrivilege(PRIVILEGES.ORG_ADMINISTRATION) &&
                        isVariablesUIEnabled() &&
                        isOrgUiEnabled() &&
                        getCurrentOrgId() === -1,
                },
            },
            __typename: 'Admin__Data' as const,
        } as unknown as AdminStatusData;

}

export interface AdminItemMeta {
    commandId: string;
    sectionId?: string;
    orgContext?: string;
    // URL sub-tab slug appended after the page slug (e.g. 'users',
    // 'early-access') so the palette lands on a specific in-page tab.
    subTabSlug?: string;
    // Canonical admin deep-link for this item (e.g.
    // '/admin/all-orgs/user-management/groups'). Stored as the recents `path`
    // so admin recents carry a real URL like every other recent item.
    url?: string;
}

/** SpotlightItem extended with admin navigation metadata. */
export type AdminSpotlightItem = SpotlightItem & { meta: AdminItemMeta };

function toSpotlightItems(commands: AdminCommand[], contextSuffix?: string, contextLabelKey?: string): AdminSpotlightItem[] {
    // Section commands without a parentId are not navigable — exclude them.
    return commands
        .filter(cmd => !(cmd.type === 'section' && !cmd.parentId))
        .map(cmd => {
            const parentLabel =
                cmd.type === 'section' && cmd.parentId
                    ? commands.find(c => c.type === 'page' && c.id === cmd.parentId)
                          ?.label
                    : undefined;

            // Build descriptionKeys so each part is translated independently at
            // render time. contextLabelKey is a real i18n key (re-translates on
            // locale switch). cmd.section / parentLabel are already-translated
            // strings from AdminCommand — tStr falls back to the string itself.
            const descriptionKeys = [
                ...(contextLabelKey ? [contextLabelKey] : []),
                ...(cmd.section ? [cmd.section] : []),
                ...(parentLabel ? [parentLabel] : []),
            ];

            let orgContext: string | undefined;
            if (contextSuffix === 'primary') {
                orgContext = 'primary-org';
            } else if (contextSuffix === 'all-org') {
                orgContext = 'all-org';
            }

            const commandId = cmd.type === 'section' ? cmd.parentId : cmd.id;
            const meta: AdminItemMeta = {
                commandId,
                sectionId: cmd.sectionId,
                orgContext,
                url: buildAdminRecentPath({
                    commandId,
                    sectionId: cmd.sectionId,
                    orgContext,
                }),
            };

            return {
                id: `${ADMIN_ITEM_PREFIX}${cmd.id}${contextSuffix ? `-${contextSuffix}` : ''}`,
                label: cmd.label,
                descriptionKeys,
                icon: IconID.COG,
                type: SpotlightItemType.ADMIN,
                keywords: cmd.searchText.split(/\s+/).filter(Boolean),
                meta,
            };
        });
}

export function isAdminSpotlightItem(item: SpotlightItem): boolean {
    return (
        item.type === SpotlightItemType.ADMIN ||
        item.type === SpotlightItemType.ADMIN_SUB_ITEM
    );
}

/**
 * Resolves admin navigation meta for a spotlight item. Fresh items carry
 * `meta` directly. Recents stored as ADMIN_SUB_ITEM encode the meta in `path`
 * in one of two shapes — chosen at store time by buildAdminRecentPath — and are
 * decoded here by shape:
 *  • Routing recents: a real admin URL ('/admin/...'), decoded via
 *    parseAdminItemPath.
 *  • Software/legacy recents: a 'commandId|sectionId|orgContext[|subTabSlug]'
 *    pipe encoding, decoded by split.
 * Returns undefined if none is present.
 */
function resolveAdminMeta(item: SpotlightItem): AdminItemMeta | undefined {
    const { meta } = item as AdminSpotlightItem;
    if (meta?.commandId) return meta;

    if (item.type === SpotlightItemType.ADMIN_SUB_ITEM && item.path) {
        // Routing URL — decode via the canonical slug maps.
        const route = parseAdminItemPath(item.path);
        if (route?.commandId) {
            return {
                commandId: route.commandId,
                orgContext: route.orgContext,
                subTabSlug: route.subTabSlug,
            };
        }
        // Legacy pipe encoding.
        if (item.path.includes('|')) {
            const [commandId, sectionId, orgContext, subTabSlug] = item.path.split(
                '|',
            );
            if (commandId) {
                return {
                    commandId,
                    sectionId: sectionId || undefined,
                    orgContext: orgContext || undefined,
                    subTabSlug: subTabSlug || undefined,
                };
            }
        }
    }
    return undefined;
}

// GetStatus-derived commands. These drive extras gating (Style / Chart /
// Homepage / ThoughtSpot AI / Search / Administration sub-items) which reference
// per-screen ids that the modern GetSidebarConfig consolidates away — so this
// source is always computed even in modern SaaS, where GetSidebarConfig only
// supplies the displayed page items (see buildSidebarCommands).
function buildAdminCommands(
    apiData: AdminStatusData,
    t: TranslateFn,
    isAllOrgContext?: boolean,
): AdminCommand[] {
    const orgId = getCurrentOrgId();
    const resolvedIsAllOrgContext = isAllOrgContext ?? orgId === -1;
    const V1_SEGMENT = isReactshellEnabled() ? '/v1' : '';

    let tabsConfig: AdminStatusData = apiData;
    if (isOrgsOnFTEnabled()) {
        tabsConfig = {
            // eslint-disable-next-line no-underscore-dangle
            __typename: apiData.__typename,
            status: {
                ...apiData.status,
                teams: { pricing: true },
            },
        } as unknown as AdminStatusData;
    }

    const sideBarConfig = getSideBarConfig(
        t,
        tabsConfig,
        isOktaEnabled(),
        false,
        resolvedIsAllOrgContext,
        isCopilotEnabled(),
        V1_SEGMENT,
    );

    return buildCommandRegistry(sideBarConfig);
}

// Modern SaaS page commands from the same GetSidebarConfig source the visual
// sidebar uses, so the displayed page items, their breadcrumb groups
// (Management / Configuration / Monitor) and per-org visibility (e.g. Feature
// Management under Primary Org) match the sidebar exactly. Returns [] when the
// response is absent so callers fall back to the GetStatus commands.
function buildSidebarCommands(
    t: TranslateFn,
    sidebarResponse: unknown,
): AdminCommand[] {
    if (!sidebarResponse) return [];
    const V1_SEGMENT = isReactshellEnabled() ? '/v1' : '';
    return buildCommandRegistry(
        getSidebarSectionsFromResponse(
            t,
            sidebarResponse as any,
            V1_SEGMENT,
        ) as any,
    );
}


/**
 * Hook that eagerly fetches admin sidebar config via Admin_getStatus
 * GraphQL query, builds the command registry, and converts to
 * SpotlightItem[] — so admin items appear in Spotlight immediately
 * without requiring a visit to /admin first.
 */
export function useAdminSpotlightItems(navService?: {
    goToUrl?: (url: string) => void;
}) {
    const { t } = useTranslation();
    // Displayed page commands: GetSidebarConfig-derived in modern SaaS (correct
    // groups + per-org visibility), GetStatus-derived otherwise.
    const [primaryCmds, setPrimaryCmds] = useState<AdminCommand[]>([]);
    const [allOrgsCmds, setAllOrgsCmds] = useState<AdminCommand[]>([]);
    // GetStatus-derived commands, always kept, used only to gate the extras
    // (Style/Chart/Homepage/AI/Search/Administration sub-items) whose per-screen
    // ids the modern GetSidebarConfig consolidates away.
    const [primaryGatingCmds, setPrimaryGatingCmds] = useState<AdminCommand[]>(
        [],
    );
    const [allOrgsGatingCmds, setAllOrgsGatingCmds] = useState<AdminCommand[]>(
        [],
    );
    // Raw GetSidebarConfig responses (modern SaaS only) kept per context so the
    // memo can enumerate server-decided in-page tabs (e.g. User Management's
    // Users/Groups/Roles) for the correct org context.
    const [primarySidebar, setPrimarySidebar] = useState<unknown>(null);
    const [allOrgsSidebar, setAllOrgsSidebar] = useState<unknown>(null);
    // Raw GetStatus application_settings per context — gates the App Settings
    // Data modeling / CSV upload tabs (no server itemTabs for those).
    const [primaryAppSettings, setPrimaryAppSettings] = useState<
        Record<string, unknown>
    >({});
    const [allOrgsAppSettings, setAllOrgsAppSettings] = useState<
        Record<string, unknown>
    >({});
    const [sageFeatureInAllOrgs, setSageFeatureInAllOrgs] = useState(false);
    const [administrationInAllOrgs, setAdministrationInAllOrgs] = useState(false);
    const [searchSpotIQInAllOrgs, setSearchSpotIQInAllOrgs] = useState(false);
    const { isAuthenticated, authenticationChangeInProgress } =
        useAuthenticatedState();

    const { getStatus: fetchStatus, data, error } = useGetStatusLazy();
    const {
        getStatus: fetchStatusAlt,
        data: dataAlt,
        error: errorAlt,
    } = useGetStatusLazy();

    // Parallel GetSidebarConfig fetches (modern SaaS): the same source the
    // visual sidebar uses, so Cmd-K breadcrumbs and per-org items match it.
    const {
        getSidebarConfig: fetchSidebar,
        data: sidebarData,
    } = useGetSidebarConfigLazyNoCache();
    const {
        getSidebarConfig: fetchSidebarAlt,
        data: sidebarDataAlt,
    } = useGetSidebarConfigLazyNoCache();
    

    useEffect(() => {
        if (authenticationChangeInProgress) return;
        if (!isAuthenticated) {
            setPrimaryCmds([]);
            setAllOrgsCmds([]);
            return;
        }
        if (!isAdminTabVisible()) return;

        const orgId = getCurrentOrgId();
        const orgsEnabled =
            isOrgUiEnabled() && (orgId === -1 || orgId === 0);
        const modern = isAdminModernExperienceEnabled();

        if (orgsEnabled) {
            // Multi-org: always fetch BOTH contexts into fixed slots regardless
            // of the session org — the primary slot ('primary' viewMode) and the
            // alt slot ('all' viewMode). This keeps each slot's org role
            // unambiguous, so Primary-Org sub-tabs (users/groups/roles) never
            // pick up the All-Orgs set (users/authentication). The all-orgs fetch
            // omits orgsEnabled to match the sidebar's tab-switch behavior.
            fetchStatus({ variables: { viewMode: 'primary', orgsEnabled } });
            fetchStatusAlt({ variables: { viewMode: 'all' } });
            if (modern) {
                fetchSidebar({ variables: { viewMode: 'primary' } });
                fetchSidebarAlt({ variables: { viewMode: 'all' } });
            }
        } else {
            // Secondary org (single context): fetch just this org's config with
            // an empty viewMode — a secondary org can't resolve 'all', which
            // would return nothing and fall back to the GetStatus breadcrumbs.
            fetchStatus({ variables: { viewMode: '', orgsEnabled } });
            if (modern) {
                fetchSidebar({ variables: { viewMode: '' } });
            }
        }
    }, [isAuthenticated, authenticationChangeInProgress]);

    useEffect(() => {
        if (!isAdminTabVisible()) return;

        const orgId = getCurrentOrgId();
        const orgsEnabled = isOrgUiEnabled() && (orgId === -1 || orgId === 0);

        // Fixed slots (see the fetch effect): the primary slot is always the
        // 'primary' viewMode response, the alt slot always the 'all' viewMode
        // response — so no org-role swap is needed here.
        const primaryOrgStatus = data?.Data;
        const allOrgStatus = dataAlt?.Data;
        const primaryOrgSidebarResp = sidebarData;
        const allOrgSidebarResp = sidebarDataAlt;

        if (primaryOrgStatus || allOrgStatus) {
            try {
                const modern = isAdminModernExperienceEnabled();

                // GetStatus commands: displayed in software, and always used to
                // gate the extras (see primaryGatingCmds/allOrgsGatingCmds).
                const primaryStatusCmds = primaryOrgStatus
                    ? buildAdminCommands(primaryOrgStatus, t, false)
                    : [];
                const allOrgStatusCmds = allOrgStatus
                    ? buildAdminCommands(allOrgStatus, t, true)
                    : [];
                // Displayed page commands: GetSidebarConfig in modern SaaS (falls
                // back to GetStatus until the sidebar response arrives).
                const primarySidebarCmds = modern
                    ? buildSidebarCommands(t, primaryOrgSidebarResp)
                    : [];
                const allOrgSidebarCmds = modern
                    ? buildSidebarCommands(t, allOrgSidebarResp)
                    : [];
                const primaryCommands = primarySidebarCmds.length
                    ? primarySidebarCmds
                    : primaryStatusCmds;
                const allOrgCommands = allOrgSidebarCmds.length
                    ? allOrgSidebarCmds
                    : allOrgStatusCmds;
                setPrimaryCmds(primaryCommands);
                setAllOrgsCmds(allOrgCommands);
                setPrimaryGatingCmds(primaryStatusCmds);
                setAllOrgsGatingCmds(allOrgStatusCmds);
                setPrimarySidebar(primaryOrgSidebarResp ?? null);
                setAllOrgsSidebar(allOrgSidebarResp ?? null);

                const primaryOrgAppSettings = (primaryOrgStatus?.status
                    ?.application_settings as any) ?? {};
                // sage_feature / administration / search are All-Orgs-only.
                const allOrgAppSettings = (allOrgStatus?.status
                    ?.application_settings as any) ?? {};
                setPrimaryAppSettings(primaryOrgAppSettings);
                setAllOrgsAppSettings(allOrgAppSettings);
                setSageFeatureInAllOrgs(!!allOrgAppSettings.sage_feature);
                setAdministrationInAllOrgs(!!allOrgAppSettings.administration);
                setSearchSpotIQInAllOrgs(!!allOrgAppSettings.search);
            } catch {
                // Fall through to default config below
            }
        } else if (error || (data && !data?.Data)) {
            if (!isSaaSCluster()) {
                try {
                    if (orgsEnabled) {
                        const allOrgCommands = buildAdminCommands(
                            getDefaultStatusConfig(CONTEXT_ALL_ID),
                            t,
                            true,
                        );
                        const primaryCommands = buildAdminCommands(
                            getDefaultStatusConfig(CONTEXT_PRIMARY_ID),
                            t,
                            false,
                        );
                        setPrimaryCmds(primaryCommands);
                        setAllOrgsCmds(allOrgCommands);
                        // Software fallback has no GetSidebarConfig; the status
                        // commands are also the gating source for extras.
                        setPrimaryGatingCmds(primaryCommands);
                        setAllOrgsGatingCmds(allOrgCommands);
                    } else {
                        const primaryCommands = buildAdminCommands(
                            getDefaultStatusConfig(),
                            t,
                        );
                        setPrimaryCmds(primaryCommands);
                        setAllOrgsCmds([]);
                        setPrimaryGatingCmds(primaryCommands);
                        setAllOrgsGatingCmds([]);
                    }
                } catch {
                    // Silently ignore config building errors
                }
            }
        }
    }, [data, error, dataAlt, errorAlt, sidebarData, sidebarDataAlt, t]);


    const adminSpotlightItems = useMemo(() => {
        const hasBothOrgContexts =
            primaryCmds.length > 0 && allOrgsCmds.length > 0;
        const allCmds = [...primaryCmds, ...allOrgsCmds];

        // The all-org context's display commands (breadcrumb source for the
        // All-Orgs-only extras below).
        const allOrgDisplayCmds = hasBothOrgContexts ? allOrgsCmds : allCmds;

        // Extras are GATED against the GetStatus commands (the modern
        // GetSidebarConfig consolidates their per-screen ids away), but their
        // BREADCRUMB is read from the displayed GetSidebarConfig commands so it
        // matches the visual sidebar (context / group / page [/ tab]).
        const buildFamilyItems = (family: ExtraFamily): SpotlightItem[] => {
            const make = (
                displayCmds: AdminCommand[],
                contextLabelKey: string | undefined,
                orgContext: string | undefined,
            ) =>
                buildExtraSubItems(
                    family.items,
                    family.navCmdId,
                    buildExtraCrumb(
                        displayCmds,
                        family.breadcrumbOwnerId,
                        family.tabLabelKey,
                        family.fallbackSectionKey,
                        contextLabelKey,
                    ),
                    orgContext,
                    t,
                );

            const items: SpotlightItem[] = [];
            if (hasBothOrgContexts) {
                if (hasCommandWithId(primaryGatingCmds, family.navCmdId)) {
                    items.push(
                        ...make(
                            primaryCmds,
                            'orgs.adminPanel.primaryorg',
                            CONTEXT_PRIMARY_ID,
                        ),
                    );
                }
                if (hasCommandWithId(allOrgsGatingCmds, family.navCmdId)) {
                    items.push(
                        ...make(
                            allOrgsCmds,
                            'orgs.adminPanel.allorg',
                            CONTEXT_ALL_ID,
                        ),
                    );
                }
            } else if (
                hasCommandWithId(
                    [...primaryGatingCmds, ...allOrgsGatingCmds],
                    family.navCmdId,
                )
            ) {
                items.push(...make(allCmds, undefined, undefined));
            }
            return items;
        };

        const allOrgLabelKey = 'orgs.adminPanel.allorg';

        // ThoughtSpot AI / Administration / Search & SpotIQ — All Orgs only,
        // gated via raw status; breadcrumb read from the all-org display command.
        const thoughtspotAIItems = sageFeatureInAllOrgs
            ? buildExtraSubItems(
                  THOUGHTSPOT_AI_SUB_ITEMS,
                  THOUGHTSPOT_AI_CMD_ID,
                  buildExtraCrumb(
                      allOrgDisplayCmds,
                      THOUGHTSPOT_AI_CMD_ID,
                      undefined,
                      'tscli.config.sage_feature.title',
                      allOrgLabelKey,
                  ),
                  CONTEXT_ALL_ID,
                  t,
              )
            : [];

        const administrationItems = administrationInAllOrgs
            ? buildExtraSubItems(
                  ADMINISTRATION_SUB_ITEMS,
                  getAdministrationCmdId(),
                  buildExtraCrumb(
                      allOrgDisplayCmds,
                      getAdministrationCmdId(),
                      undefined,
                      'adminSection.administration.name',
                      allOrgLabelKey,
                  ),
                  CONTEXT_ALL_ID,
                  t,
              )
            : [];

        const searchSpotIQItems = searchSpotIQInAllOrgs
            ? buildExtraSubItems(
                  SEARCH_SPOTIQ_SUB_ITEMS,
                  getSearchSpotIQCmdId(),
                  buildExtraCrumb(
                      allOrgDisplayCmds,
                      getSearchSpotIQCmdId(),
                      undefined,
                      'tscli.config.search.title',
                      allOrgLabelKey,
                  ),
                  CONTEXT_ALL_ID,
                  t,
              )
            : [];

        const extraItems = [
            ...CUSTOMIZATION_FAMILIES.flatMap(buildFamilyItems),
            ...thoughtspotAIItems,
            ...administrationItems,
            ...searchSpotIQItems,
        ];

        // In-page tabs (User Management: Users/Groups/Roles/Authentication;
        // Feature Management: General/Early Access) as navigable Cmd-K entries.
        // Each context reads its own server-decided tabs and lands on the
        // specific sub-tab via meta.subTabSlug. primaryCmds is the primary-org
        // context (isAllOrg=false), allOrgsCmds the all-orgs one — mirroring the
        // toSpotlightItems labeling below.
        const pageTabItems = hasBothOrgContexts
            ? [
                  ...buildPageTabsForContext(
                      primaryCmds,
                      primarySidebar,
                      false,
                      t,
                      primaryAppSettings,
                      'orgs.adminPanel.primaryorg',
                      CONTEXT_PRIMARY_ID,
                  ),
                  ...buildPageTabsForContext(
                      allOrgsCmds,
                      allOrgsSidebar,
                      true,
                      t,
                      allOrgsAppSettings,
                      'orgs.adminPanel.allorg',
                      CONTEXT_ALL_ID,
                  ),
              ]
            : buildPageTabsForContext(
                  allCmds,
                  primarySidebar ?? allOrgsSidebar,
                  getCurrentOrgId() === -1,
                  t,
                  primaryAppSettings ?? allOrgsAppSettings,
              );

        if (hasBothOrgContexts) {
            return [
                ...toSpotlightItems(primaryCmds, 'primary', 'orgs.adminPanel.primaryorg'),
                ...toSpotlightItems(allOrgsCmds, 'all-org', 'orgs.adminPanel.allorg'),
                ...extraItems,
                ...pageTabItems,
            ];
        }
        return [...toSpotlightItems(allCmds), ...extraItems, ...pageTabItems];
    }, [
        primaryCmds,
        allOrgsCmds,
        primaryGatingCmds,
        allOrgsGatingCmds,
        primarySidebar,
        allOrgsSidebar,
        primaryAppSettings,
        allOrgsAppSettings,
        sageFeatureInAllOrgs,
        administrationInAllOrgs,
        searchSpotIQInAllOrgs,
        t,
    ]);

    const handleAdminItemSelect = useCallback(
        (item: SpotlightItem) => {
            const meta = resolveAdminMeta(item);
            if (!meta?.commandId) {
                // Decode failed (malformed recents entry) — fall back to /admin
                // rather than doing nothing.
                navService?.goToUrl?.('/admin');
                return;
            }

            // Cache once: the flag lookup crosses a module boundary, and a
            // single read keeps both branches below consistent even if a test
            // mock mutates the flag between calls within one render.
            const routingEnabled = isAdminRoutingEnabled();

            // Legacy (non-routing) only: persist target tab + org context so the
            // admin sidebar picks them up on mount (admin MFE may not be loaded
            // yet). When routing is enabled the URL navigated to below carries
            // both (context prefix + tab), so no sessionStorage handoff is needed
            // and the routing path stays storage-free.
            if (!routingEnabled) {
                sessionStorage.setItem('AdminLandingTabId', meta.commandId);
                if (meta.orgContext) {
                    sessionStorage.setItem(
                        'AdminLandingOrgContext',
                        meta.orgContext,
                    );
                }
            }

            // When routing is enabled, navigate to the CANONICAL slug URL so
            // app.tsx resolves the same screenID the sidebar approves. tabIdToSlug
            // maps consolidated customization sub-screens to `customization/<sub>`
            // (e.g. email-customizations → customization/email); navigating to the
            // raw tabId instead makes app.tsx derive a different screenID than the
            // sidebar (which reads the rewritten canonical URL), so the permission
            // gate never opens and the pane renders blank. The slug can contain a
            // `/` (sub-tab), so it must not be percent-encoded.
            if (routingEnabled && meta.commandId) {
                const prefix =
                    meta.orgContext === 'all-org' ? '/admin/all-orgs' : '/admin';
                // Append the sub-tab slug (if any) so the palette lands on a
                // specific in-page tab (e.g. user-management/groups,
                // feature-management/early-access). The container reads it from
                // the URL on mount.
                const subTab = meta.subTabSlug ? `/${meta.subTabSlug}` : '';
                navService?.goToUrl?.(
                    `${prefix}/${tabIdToSlug(meta.commandId)}${subTab}`,
                );
            } else {
                navService?.goToUrl?.('/admin');
            }

            // Dispatch synchronously: if admin sidebar is already mounted its listener
            // handles this immediately; if not yet mounted, the localStorage values above
            // are read on mount and cover that case.
            window.dispatchEvent(
                new CustomEvent(ADMIN_COMMAND_PALETTE_NAVIGATE_EVENT, {
                    detail: {
                        commandId: meta.commandId,
                        sectionId: meta.sectionId,
                        orgContext: meta.orgContext,
                        subTabSlug: meta.subTabSlug,
                    },
                }),
            );
        },
        [navService],
    );

    return { adminSpotlightItems, handleAdminItemSelect };
}
